/**
 * Firebase Cloud Messaging (FCM) push notification utility.
 *
 * Setup: Add to frontend/.env:
 *   REACT_APP_FIREBASE_API_KEY=...
 *   REACT_APP_FIREBASE_AUTH_DOMAIN=...
 *   REACT_APP_FIREBASE_PROJECT_ID=...
 *   REACT_APP_FIREBASE_MESSAGING_SENDER_ID=...
 *   REACT_APP_FIREBASE_APP_ID=...
 *   REACT_APP_FIREBASE_VAPID_KEY=...
 *
 * All operations gracefully no-op if Firebase is not configured.
 */

import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";

const API = process.env.REACT_APP_BACKEND_URL;

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

let app = null;
let messaging = null;
let _ready = false;

export async function initFirebase() {
  if (_ready) return true;
  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    console.info("[Push] Firebase not configured — push notifications disabled");
    return false;
  }
  try {
    const supported = await isSupported();
    if (!supported) {
      console.info("[Push] Firebase messaging not supported in this browser");
      return false;
    }
    app = initializeApp(firebaseConfig);
    messaging = getMessaging(app);
    _ready = true;
    console.info("[Push] Firebase initialized");
    return true;
  } catch (e) {
    console.warn("[Push] Firebase init failed:", e);
    return false;
  }
}

export async function requestPushPermission() {
  if (!_ready && !(await initFirebase())) return null;

  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.info("[Push] Permission denied");
      return null;
    }

    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    if (!token) {
      console.warn("[Push] No token received");
      return null;
    }

    console.info("[Push] Token obtained:", token.slice(0, 16) + "...");
    return token;
  } catch (e) {
    console.warn("[Push] Token request failed:", e);
    return null;
  }
}

export async function registerPushToken(authToken) {
  const pushToken = await requestPushPermission();
  if (!pushToken || !authToken) return false;

  try {
    const res = await fetch(`${API}/api/push/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ token: pushToken, platform: "web" }),
    });
    return res.ok;
  } catch (e) {
    console.warn("[Push] Registration failed:", e);
    return false;
  }
}

export function onPushMessage(callback) {
  if (!_ready || !messaging) return () => {};
  return onMessage(messaging, (payload) => {
    console.info("[Push] Message received:", payload);
    callback(payload);
  });
}

export function isFirebaseConfigured() {
  return !!firebaseConfig.apiKey && !!firebaseConfig.projectId;
}
